import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { Separator } from "../components/ui/separator";
import { Envelope, ArrowLeft, LinkedinLogo } from "@phosphor-icons/react";

const SBL_LOGO = "/sbl-logo.jpeg";

const TEAM_MEMBERS = [
  { img: "/team_face_1.jpeg", name: "Sola Adeola Amudipe", role: "Founder & Chief Learning Architect", email: "chiefarchitect@schoolbuilderlab.net.ng", featured: true },
  { img: "/team_face_2.jpeg", name: "Afolabi Omotesho", role: "Content Creator", email: "contentcreator@schoolbuilderlab.net.ng" },
  { img: "/team_face_3.jpeg", name: "Oluwaseyi Anifowose", role: "Partnership & Growth Manager", email: "partnerships@schoolbuilderlab.net.ng" },
  { img: "/team_face_11.jpeg", name: "Chiaka Unegbu", role: "Strategic Lead", email: "strategiclead@schoolbuilderlab.net.ng" },
  { img: "/team_face_4.jpeg", name: "Light Chime", role: "Content Development Lead", email: "contentdev@schoolbuilderlab.net.ng" },
  { img: "/team_face_5.jpeg", name: "Afeez Iyiola", role: "Advisory Support", email: "advisory@schoolbuilderlab.net.ng" },
  { img: "/team_face_6.jpeg", name: "Akin Adewolu", role: "Mentor & Facilitator Liaison", email: "mentorship@schoolbuilderlab.net.ng" },
  { img: "/team_face_7.jpeg", name: "Adebayo Alomaja", role: "Project Manager", email: "projectmanager@schoolbuilderlab.net.ng" },
  { img: "/team_face_9.jpeg", name: "Bisayo Adewole", role: "Content & Learning Experience Designer", email: "lxdesigner@schoolbuilderlab.net.ng" },
  { img: "/team_face_10.jpeg", name: "Olusegun Akintade", role: "LMS & Tech Support Lead", email: "techsupport@schoolbuilderlab.net.ng" },
  { img: null, name: "Folaranmi Ajayi", role: "Growth & Marketing Lead", email: "growth@schoolbuilderlab.net.ng" },
  { img: null, name: "Oyekan Temitope", role: "Content Development Intern", email: "intern@schoolbuilderlab.net.ng" },
];

const BOARD_MEMBERS = [
  { name: "Sola Adeola Amudipe", role: "Chief Learning Architect & Chairperson", img: "/team_face_1.jpeg" },
  { name: "Kamal Ahmed", role: "Board Advisor - Strategy, Quality & Institutional Excellence", img: "/board_face_0.jpeg" },
  { name: "Jide Ayegbusi", role: "Board Advisor - Growth, Partnerships & Expansion", img: "/board_face_1.jpeg" },
  { name: "Jatau Vincent John", role: "Board Advisor - Policy, Systems & Institutional Partnerships", img: "/board_face_2.jpeg" },
  { name: "Oluwaseyi Anifowose", role: "Board Advisor - Community & Engagement Strategy", img: "/team_face_3.jpeg" },
  { name: "Beere Mo", role: "Board Advisor - Content & Strategic Communication", img: "/board_beere_mo.jpeg" },
  { name: "Bunmi Lawson", role: "Board Advisor - Finance & Sustainability", img: "/board_bunmi_lawson.jpeg" },
  { name: "Olusegun Akintade", role: "Director, Operations and Administration", img: "/team_face_10.jpeg" },
];

export default function TeamPage() {
  const founder = TEAM_MEMBERS.find(m => m.featured);
  const coreTeam = TEAM_MEMBERS.filter(m => !m.featured);

  const getInitials = (name) => name.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase();

  return (
    <div className="min-h-screen bg-white">
      {/* Nav */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={SBL_LOGO} alt="SBL" className="w-9 h-9 object-contain rounded-sm" />
            <span className="text-base sm:text-lg font-bold tracking-tight text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>
              School Builder Lab Africa
            </span>
          </Link>
          <Link to="/" className="text-sm text-slate-600 hover:text-slate-900 flex items-center gap-1">
            <ArrowLeft size={14} /> Back to Home
          </Link>
        </div>
      </header>

      {/* Hero Banner */}
      <div className="bg-slate-950 text-white py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-amber-400 mb-3">Our People</p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
            The Team Behind School Builder Lab Africa
          </h1>
          <p className="text-base text-white/70 mt-4 max-w-xl mx-auto">
            Passionate educators, strategists, and professionals committed to transforming education across Africa.
          </p>
        </div>
      </div>

      {/* ─── MEET THE TEAM ─── */}
      <section id="team" className="py-16 sm:py-20 md:py-24" data-testid="team-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <p className="text-xs font-semibold uppercase tracking-widest text-blue-800 mb-2">Meet the Team</p>
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-950 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Our Core Team
          </h2>
          <p className="text-base text-slate-600 max-w-2xl mb-12">
            The dedicated individuals driving the School Builder Lab Africa mission every day.
          </p>

          {/* Founder Featured */}
          {founder && (
            <div className="flex justify-center mb-14">
              <div className="text-center max-w-[260px]" data-testid="team-founder">
                <div className="w-44 h-44 mx-auto mb-4 rounded-full overflow-hidden border-4 border-amber-400/40 shadow-lg">
                  <img src={founder.img} alt={founder.name} className="w-full h-full object-cover" />
                </div>
                <p className="text-lg font-bold text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>{founder.name}</p>
                <p className="text-sm font-medium text-blue-800 mt-0.5">{founder.role}</p>
                <a href={`mailto:${founder.email}`} className="text-xs text-slate-500 hover:text-blue-800 mt-1 inline-flex items-center gap-1 transition-colors">
                  <Envelope size={12} /> {founder.email}
                </a>
              </div>
            </div>
          )}

          {/* Core Team Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-8 sm:gap-10">
            {coreTeam.map((member, i) => (
              <div key={i} className="group text-center" data-testid={`team-member-${i}`}>
                <div className="w-32 h-32 sm:w-36 sm:h-36 mx-auto mb-3 rounded-full overflow-hidden border-2 border-slate-200 group-hover:border-blue-800 transition-all duration-300 shadow-md group-hover:shadow-lg bg-slate-100 flex items-center justify-center">
                  {member.img ? (
                    <img src={member.img} alt={member.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  ) : (
                    <span className="text-slate-500 text-xl font-bold">{getInitials(member.name)}</span>
                  )}
                </div>
                <p className="text-sm font-semibold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>{member.name}</p>
                <p className="text-xs text-blue-800/80 mt-0.5">{member.role}</p>
                {member.email && (
                  <a href={`mailto:${member.email}`} className="text-xs text-slate-500 hover:text-blue-800 mt-1 inline-flex items-center gap-1 transition-colors">
                    <Envelope size={10} /> {member.email}
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <Separator />

      {/* ─── BOARD OF ADVISORS ─── */}
      <section id="board" className="py-16 sm:py-20 md:py-24 bg-slate-50" data-testid="board-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <p className="text-xs font-semibold uppercase tracking-widest text-blue-800 mb-2">Governance</p>
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-950 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Board of Advisors
          </h2>
          <p className="text-base text-slate-600 max-w-2xl mb-12">
            The strategic leaders guiding the vision and governance of School Builder Lab Africa.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {BOARD_MEMBERS.map((member, i) => (
              <Card key={i} className="border border-slate-200 rounded-none shadow-sm hover:shadow-md transition-all" data-testid={`board-member-${i}`}>
                <CardContent className="p-6 flex items-center gap-5">
                  <div className="w-20 h-20 rounded-full overflow-hidden flex-shrink-0 border-2 border-slate-200 bg-slate-100 flex items-center justify-center">
                    {member.img ? (
                      <img src={member.img} alt={member.name} className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-slate-400 text-2xl font-semibold">
                        {member.name.split(" ").map(n => n[0]).join("").slice(0, 2)}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-base font-semibold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>{member.name}</p>
                    <p className="text-sm text-blue-800 mt-0.5">{member.role}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 text-slate-400 py-8 text-center text-xs">
        <p>&copy; {new Date().getFullYear()} School Builder Lab Africa. All rights reserved.</p>
      </footer>
    </div>
  );
}
